import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileDown, AlertTriangle, History } from "lucide-react";
import { fmtCurrency } from "@/utils/format";
import CsvImport from "@/components/CsvImport";
import Pagination from "@/components/Pagination";
import PageHeader from "@/components/PageHeader";
import ProductDetailDialog from "./ProductDetailDialog";

const empty = { name: "", sku: "", price: 0, stock: 0, unit: "adet", category: "" };
const PAGE_SIZE = 10;

export default function Products() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "sales";
  const canSeePrice = canManage; // only admin/sales see price
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState("");

  const load = async () => {
    setRefreshing(true);
    try {
      const [p, c] = await Promise.all([
        api.get(`/products?_ts=${Date.now()}`),
        api.get("/settings/product-categories").catch(() => ({ data: { product_categories: [] } })),
      ]);
      setRows(p.data);
      setCategories(c.data?.product_categories || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, price: Number(form.price), stock: Number(form.stock) };
      if (editingId) await api.put(`/products/${editingId}`, payload);
      else await api.post("/products", payload);
      toast.success(editingId ? "Ürün güncellendi" : "Ürün eklendi");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const edit = (r) => { setForm({ ...r, price: r.price ?? 0 }); setEditingId(r.id); setOpen(true); };
  const remove = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Admin-only: wipe the entire product catalog. Two-step confirm to prevent
  // fat-fingering — the user must literally type "SIL" to proceed.
  const removeAll = async () => {
    if (rows.length === 0) { toast.info("Silinecek ürün yok"); return; }
    if (!window.confirm(`${rows.length} ürünün TAMAMI silinecek. Devam edilsin mi?`)) return;
    const typed = window.prompt("Onaylamak için 'SIL' yazın:");
    if ((typed || "").trim().toLocaleUpperCase("tr-TR") !== "SIL") {
      toast.info("İşlem iptal edildi");
      return;
    }
    try {
      const { data } = await api.delete("/products");
      toast.success(`${data.deleted} ürün silindi`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/products/export/xlsx", { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replaceAll("-", "");
      a.download = `urunler-${stamp}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      toast.success(`${rows.length} ürün Excel olarak indirildi`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İndirilemedi"); }
  };

  const filtered = useMemo(
    () => rows.filter((r) =>
      (r.name.toLocaleLowerCase("tr-TR").includes(q.toLocaleLowerCase("tr-TR")) ||
       r.sku.toLocaleLowerCase("tr-TR").includes(q.toLocaleLowerCase("tr-TR"))) &&
      (!filterCat || (r.category || "") === filterCat)
    ),
    [rows, q, filterCat]
  );
  useEffect(() => { setPage(1); }, [q, filterCat, rows.length]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="products-page">
      <PageHeader
        kicker="Katalog"
        title="Ürünler"
        onRefresh={load}
        refreshing={refreshing}
        actions={canManage ? (
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  className="rounded-none border-2 border-[#09090B]"
                  onClick={exportXlsx}
                  data-testid="products-export-xlsx-btn"
                >
                  <FileDown size={16} className="mr-2" /> Excel İndir
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
                  onClick={removeAll}
                  data-testid="products-delete-all-btn"
                >
                  <AlertTriangle size={16} className="mr-2" /> Tümünü Sil
                </Button>
              </>
            )}
            <CsvImport
              endpoint="/products/import"
              templateUrl="/products/import/template"
              templateName="urun_sablonu.csv"
              title="Ürün CSV İçe Aktar"
              testidPrefix="products-import"
              columns={["sku", "name", "unit", "stock", "price"]}
              onImported={load}
            />
            <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={() => { setForm(empty); setEditingId(null); setOpen(true); }} data-testid="new-product-btn">
              <Plus size={16} className="mr-2" /> Yeni Ürün
            </Button>
          </div>
        ) : null}
      />

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-[10px] font-mono uppercase text-neutral-500">Ara</Label>
          <Input placeholder="İsim veya SKU" value={q} onChange={(e) => setQ(e.target.value)} data-testid="products-search"
            className="max-w-sm rounded-none border-2 border-[#09090B] focus-visible:ring-0" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase text-neutral-500">Kategori</Label>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="min-w-[160px] rounded-none border-2 border-[#09090B] font-mono text-sm px-2 py-2 bg-white focus-visible:ring-0"
            data-testid="products-category-filter"
          >
            <option value="">Tümü</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">SKU</TableHead>
              <TableHead className="font-mono uppercase text-xs">Ürün</TableHead>
              <TableHead className="font-mono uppercase text-xs">Kategori</TableHead>
              <TableHead className="font-mono uppercase text-xs">Birim</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">Stok</TableHead>
              {canSeePrice && <TableHead className="font-mono uppercase text-xs text-right">Fiyat</TableHead>}
              {canManage && <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((r) => (
              <TableRow
                key={r.id}
                data-testid={`product-row-${r.id}`}
                className="cursor-pointer hover:bg-neutral-50"
                onClick={(e) => {
                  // İşlem hücresindeki butonlara tıklamayı yutma
                  if (e.target.closest("button")) return;
                  setDetailProduct(r);
                }}
              >
                <TableCell className="font-mono">{r.sku}</TableCell>
                <TableCell className="font-semibold flex items-center gap-2">
                  {r.name}
                  <History size={12} className="text-neutral-400" />
                </TableCell>
                <TableCell className="text-sm font-mono">
                  {r.category ? (
                    <span className="inline-block px-1.5 py-0.5 border border-neutral-400 text-neutral-700 text-[10px] uppercase">{r.category}</span>
                  ) : (
                    <span className="text-neutral-400 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{r.unit}</TableCell>
                <TableCell className="text-right font-mono">{r.stock}</TableCell>
                {canSeePrice && <TableCell className="text-right font-mono">{fmtCurrency(r.price)}</TableCell>}
                {canManage && (
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => edit(r)} data-testid={`edit-product-${r.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-product-${r.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={canSeePrice ? 7 : 6} className="text-center py-8 text-neutral-500">Kayıt yok</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} testidPrefix="products-pagination" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-2 border-[#09090B] bg-white">
          <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">{editingId ? "Ürün Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label className="text-xs font-mono uppercase">Ad</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none border-2 border-[#09090B]" data-testid="product-name-input" /></div>
            <div><Label className="text-xs font-mono uppercase">SKU</Label>
              <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                className="rounded-none border-2 border-[#09090B] font-mono" data-testid="product-sku-input" /></div>
            <div><Label className="text-xs font-mono uppercase">Kategori</Label>
              <select
                value={form.category || ""}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full rounded-none border-2 border-[#09090B] font-mono text-sm px-2 py-2 bg-white focus-visible:ring-0"
                data-testid="product-category-input"
              >
                <option value="">— Seçiniz —</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {(!categories || categories.length === 0) && (
                <div className="mt-1 text-[10px] font-mono text-neutral-500">
                  Henüz tanımlı kategori yok. Ayarlar &rarr; Ürün Kategorileri'nden ekleyebilirsiniz.
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs font-mono uppercase">Birim</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className="rounded-none border-2 border-[#09090B]" data-testid="product-unit-input" /></div>
              <div><Label className="text-xs font-mono uppercase">Stok</Label>
                <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  className="rounded-none border-2 border-[#09090B] font-mono" data-testid="product-stock-input" /></div>
              <div><Label className="text-xs font-mono uppercase">Fiyat</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="rounded-none border-2 border-[#09090B] font-mono" data-testid="product-price-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={save} data-testid="save-product-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProductDetailDialog
        product={detailProduct}
        open={!!detailProduct}
        onOpenChange={(v) => { if (!v) setDetailProduct(null); }}
        canSeePrice={canSeePrice}
      />
    </div>
  );
}
